import * as React from 'react';
import { Box, Container, Typography, Grid, Link, useTheme, styled } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { TaskAltOutlined } from '@mui/icons-material';

const PatternOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundImage: "url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMTAwIDAgTCAwIDAgMCAxMDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIyZDNlZSIgc3Ryb2tlLXdpZHRoPSIwLjUiIG9wYWNpdHk9IjAuMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==\")",
  opacity: 0.3,
  zIndex: 0,
});

const FeatureCard = ({ icon: Icon, title, description, gradient }) => (
  <Box
    sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(8px)',
      borderRadius: 3,
      p: { xs: 2.5, md: 3 },
      border: '1px solid rgba(255, 255, 255, 0.2)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        width: { xs: 44, md: 48 },
        height: { xs: 44, md: 48 },
        background: gradient,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ width: { xs: 24, md: 28 }, height: { xs: 24, md: 28 }, color: 'grey.900' }} />
    </Box>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'white', fontSize: { xs: '1.25rem', md: '1.5rem' }, flexGrow: 1 }}>
      {title}
    </Typography>
    <Typography sx={{ color: '#90CAF9', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
      {description}
    </Typography>
  </Box>
);


export default function LivePreview() {
  const theme = useTheme();

  const backgroundGradient = 'linear-gradient(to bottom right, #1F2937, #1E3A8A, #3730A3)';
  const greenGradient = 'linear-gradient(to right, #4ADE80, #10B981)';
  const cyanBlueGradient = 'linear-gradient(to right, #22D3EE, #3B82F6)';
  const purplePinkGradient = 'linear-gradient(to right, #C084FC, #EC4899)';
  const accentGreenColor = '#10B981';
  const lightGreenColor = '#A7F3D0';

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 12 },
        background: backgroundGradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <PatternOverlay />

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, position: 'relative', zIndex: 10 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 6, md: 10 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              background: greenGradient,
              px: { xs: 2.5, md: 3 },
              py: 1,
              borderRadius: 999,
              mb: 3,
            }}
          >
            <PlayArrowIcon sx={{ width: 20, height: 20, color: 'grey.900' }} />
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                color: 'grey.900',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                textTransform: 'uppercase',
                letterSpacing: 1.5,
              }}
            >
              See It In Action
            </Typography>
          </Box>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            To Watch NC Pro Solve a Real Quality Issue
            <br />
            <Box component="span" sx={{
              background: `linear-gradient(to right, ${lightGreenColor}, ${lightGreenColor})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
            }}>
              in Under 5 Minutes
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              color: '#BFDBFE',
            }}
          >
            See how our AI analyzes, identifies root causes, and generates reports faster than you can finish your coffee
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 880, mx: 'auto', mb: 6 }}>
          <Grid
            container
            spacing={{ xs: 2, md: 3 }}
            justifyContent="center"
            alignItems="stretch"
          >
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={PsychologyIcon}
                title="AI Analysis"
                description="Watch AI process 247+ data points and identify root causes in real-time"
                gradient={greenGradient}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={AutoAwesomeIcon}
                title="Smart Suggestions"
                description="See how AI provides corrective action recommendations based on proven solutions"
                gradient={cyanBlueGradient}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={DescriptionIcon}
                title="Instant Reports"
                description="Watch a complete 8D report generate in 30 seconds with one click"
                gradient={purplePinkGradient}
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(12px)',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: { xs: '100%', md: 640 },
            mx: 'auto',
            width: { xs: 'calc(100% - 32px)', md: 'auto' },
            marginLeft: { xs: '16px', md: 'auto' },
            marginRight: { xs: '16px', md: 'auto' },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, textAlign: 'center', color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            What Happens in This Demo
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, md: 3 } }}>
            {[
              { time: 'Problem Input (10 seconds)', detail: 'Enter a quality issue description', step: '1' },
              { time: 'AI Analysis (2 minutes)', detail: 'Watch AI process data and identify root causes', step: '2' },
              { time: 'Corrective Actions (1 minute)', detail: 'Review AI-suggested corrective actions', step: '3' },
              { time: 'Report Generation (30 seconds)', detail: 'Generate complete 8D report with one click', step: '4' },
            ].map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 28, md: 32 },
                    height: { xs: 28, md: 32 },
                    backgroundColor: accentGreenColor,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    {item.step}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'white', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    {item.time}
                  </Typography>
                  <Typography sx={{ color: '#90CAF9', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, pt: { xs: 2.5, md: 3 }, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <TaskAltOutlined sx={{ width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 }, color: '#4ADE80' }} />
              <Typography variant="body1" sx={{ fontWeight: 700, color: lightGreenColor, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                Total Time: 4 minutes 32 seconds
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 5, md: 6 }, textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              color: '#BFDBFE',
              mb: 4,
            }}
          >
            Ready to experience this speed in your own environment?
          </Typography>
          <Link
            href="#demo"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              background: greenGradient,
              color: 'grey.900',
              px: { xs: 3.5, sm: 5 },
              py: { xs: 2.25, sm: 3 },
              borderRadius: 3,
              fontWeight: 700,
              fontSize: { xs: '1rem', md: '1.125rem' },
              textAlign: 'center',
              transition: 'all 0.3s',
              boxShadow: theme.shadows[15],
              '&:hover': {
                background: `linear-gradient(to right, #86EFAC, #6EE7B7)`,
                transform: 'scale(1.02)',
                boxShadow: theme.shadows[18],
              },
            }}
          >


            Schedule Your Live Demo
            <PlayArrowIcon sx={{ width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } }} />
          </Link>
        </Box>
      </Container>
    </Box>
  );
}
